import firebase from 'firebase/app'
import 'firebase/firestore'
import _ from 'lodash'
import axios from 'axios'
import { getTeamByCompany } from './core/cacheService'
import { getCompanyTimezone, toCompanyDate } from './utils/companyTimezone'
import {
  periodRange, personPeriodRange, tallyFeedback, tallyRequests,
  topMention, ratingDelta, tallyJourneys, recognitionStats,
  computeInputHash, narrativeStatus, latestPictureDocs, tallyBoostsByPerson,
} from '@/utils/developmentPicture'

const evidenceRequests: { [period: string]: Promise<any> } = {}

function rosterPeriodKey(period: string): string {
  return period === 'Past year' ? '12m' : '6m'
}

function emailKeyOf(email: string): string {
  return String(email || '').replace(/\./g, '||')
}

function applyPictureToPayload(payload: any, docBody: any) {
  if (!payload || !docBody || !docBody.email) return
  const emailKey = emailKeyOf(docBody.email)
  payload.pictureDocs = { ...(payload.pictureDocs || {}), [emailKey]: docBody }
  const nowMs = Date.now()
  payload.rows = (payload.rows || []).map((row: any) => {
    if (!row || row.emailKey !== emailKey) return row
    return {
      ...row,
      summaryStatus: narrativeStatus(
        docBody,
        row.inputHash,
        row.feedbackEvents,
        nowMs,
        row.managerReviews || 0,
      ),
    }
  })
}

// Evidence for the Skills → Development tab. All heavy lifting lives in
// @/utils/developmentPicture (unit-tested); this module only fetches and joins.
export default {
  namespaced: true,
  state: {
    loading: true,
    period: 'Past 6-months',
    range: null as any,
    rows: [] as any[],
    // per-emailKey rollups the person page reads
    current: {} as any,
    prior: {} as any,
    requests: {} as any,
    recognition: {} as any,
    journeyDocs: [] as any[],
    // narrative cache docs from development_pictures, keyed by emailKey —
    // populated by the roster's latestPictureDocs, read for the Summary column
    pictureDocs: {} as any,
    // session caches
    evidenceCache: {} as any,
    journeysFetched: false,
    // ---- person page (v2) ----
    personKeyLoaded: '', // emailKey whose evidence is loaded
    personMonths: 6, // 6 | 12 (person-page toggle)
    personLoading: false,
    // { feedbackAll, boostDocs, journeyDocs, recognitionDocs, reflectionDocs }
    personEvidence: null as any,
    personRequestId: 0, // race token — bumped per person-page request
    pictureDoc: null as any, // cached development_pictures doc for person+period
    // 'none' | 'loading' | 'not_generated' | 'fresh' | 'generating' | 'error'
    pictureStatus: 'none',
  },
  mutations: {
    setLoading(state: any, v: boolean) { state.loading = v },
    setPeriod(state: any, v: string) { state.period = v },
    setEvidence(state: any, p: any) {
      state.range = p.range
      state.rows = p.rows
      state.current = p.current
      state.prior = p.prior
      state.requests = p.requests
      state.recognition = p.recognition
      state.pictureDocs = p.pictureDocs
    },
    setJourneyDocs(state: any, docs: any[]) {
      state.journeyDocs = docs
      state.journeysFetched = true
    },
    upsertGeneratedPicture(state: any, docBody: any) {
      if (!docBody || !docBody.email || !docBody.periodKey) return
      const emailKey = emailKeyOf(docBody.email)
      Object.keys(state.evidenceCache || {}).forEach((period) => {
        if (rosterPeriodKey(period) === docBody.periodKey) {
          applyPictureToPayload(state.evidenceCache[period], docBody)
        }
      })
      if (rosterPeriodKey(state.period) === docBody.periodKey) {
        state.pictureDocs = { ...(state.pictureDocs || {}), [emailKey]: docBody }
        const nowMs = Date.now()
        state.rows = (state.rows || []).map((row: any) => {
          if (!row || row.emailKey !== emailKey) return row
          return {
            ...row,
            summaryStatus: narrativeStatus(
              docBody,
              row.inputHash,
              row.feedbackEvents,
              nowMs,
              row.managerReviews || 0,
            ),
          }
        })
      }
    },
    // Kept for external evidence changes where a local row update is not enough.
    invalidateEvidenceCache(state: any) { state.evidenceCache = {} },
    // ---- person page (v2) ----
    setPersonMonths(state: any, v: number) { state.personMonths = v },
    setPersonLoading(state: any, v: boolean) { state.personLoading = v },
    setPersonEvidence(state: any, p: any) {
      state.personEvidence = p.evidence
      state.personKeyLoaded = p.emailKey
    },
    bumpPersonRequest(state: any) { state.personRequestId++ },
    setPersonPictureDoc(state: any, doc: any) { state.pictureDoc = doc },
    setPictureStatus(state: any, s: string) { state.pictureStatus = s },
  },
  getters: {
    loading: (state: any) => state.loading,
    period: (state: any) => state.period,
    range: (state: any) => state.range,
    rows: (state: any) => state.rows,
    // NOTE: the v1 curried `personEvidence` getter was removed with the v2
    // person-page rebuild (Task 5). The v2 page reads the `personEvidence`
    // STATE field directly (state.development.personEvidence).
  },
  actions: {
    async getEvidence({ commit, state, rootState, dispatch }: any, value: any) {
      const period = value && value.period ? value.period : value
      const force = !!(value && value.force)
      commit('setPeriod', period)
      if (!force && state.evidenceCache[period]) {
        commit('setEvidence', state.evidenceCache[period])
        commit('setLoading', false)
        return
      }
      if (!force && evidenceRequests[period]) {
        commit('setLoading', true)
        try {
          const cached = await evidenceRequests[period]
          if (state.period === period) commit('setEvidence', cached)
        } catch (error) {
          console.error('Error fetching development evidence:', error)
          if (state.period === period) {
            commit('setEvidence', {
              range: null, rows: [], current: {}, prior: {},
              requests: {}, recognition: {}, pictureDocs: {},
            })
          }
        } finally {
          if (state.period === period) commit('setLoading', false)
        }
        return
      }
      commit('setLoading', true)
      const request = (async () => {
        const company = rootState.authen.user.company
        const [, teams, timezone] = await Promise.all([
          dispatch('main/getuser', false, { root: true }),
          getTeamByCompany(company),
          getCompanyTimezone(company),
        ])
        const allUser = rootState.main.allUser || {}
        const range = periodRange(period, toCompanyDate(new Date(), timezone))

        const fs = firebase.firestore()
        const feedbackQ = fs.collection('peerfeedback')
          .where('company', '==', company)
          .where('date', '>=', range.start)
          .where('date', '<=', range.end).get()
        const prevQ = range.comparable
          ? fs.collection('peerfeedback')
            .where('company', '==', company)
            .where('date', '>=', range.prevStart)
            .where('date', '<=', range.prevEnd).get()
          : Promise.resolve(null)
        const requestQ = fs.collection('users_questions')
          .where('company', '==', company)
          .where('quest_id', '==', '-L1u2Xa5_pSCnitgF3u6')
          .where('date', '>=', range.start)
          .where('date', '<=', range.end).get()
        const recogQ = fs.collection('here_point')
          .where('company', '==', company)
          .where('statusrow', '==', 'sendcoin')
          .where('date', '>=', range.start)
          .where('date', '<=', range.end).get()
        const journeysQ = state.journeysFetched
          ? Promise.resolve(null)
          : fs.collection('develop_skills').where('company', '==', company).get()
        // The person page (v2) writes narrative docs under the deterministic
        // periodKey '6m' / '12m'. Map the roster preset to that vocabulary so
        // v2-generated pictures surface here. 'Past year' aligns to '12m'; every
        // other preset falls back to the '6m' default the person page opens with.
        const pictureKey = rosterPeriodKey(period)
        const picturesQ = fs.collection('development_pictures')
          .where('company', '==', company)
          .where('periodKey', '==', pictureKey).get()
        // Manager performance boosts (send_review_gem). Equality-only query to
        // avoid a company+createdate composite index; the window filter runs
        // client-side in tallyBoostsByPerson.
        const boostsQ = fs.collection('send_review_gem').where('company', '==', company).get()

        const [feedbackSnap, prevSnap, requestSnap, recogSnap, journeySnap, picturesSnap, boostsSnap] =
          await Promise.all([feedbackQ, prevQ, requestQ, recogQ, journeysQ, picturesQ, boostsQ])

        if (journeySnap) {
          commit('setJourneyDocs', journeySnap.docs.map((d: any) => d.data()))
        }

        const current = tallyFeedback(feedbackSnap.docs.map((d: any) => d.data()))
        const prior = prevSnap ? tallyFeedback(prevSnap.docs.map((d: any) => d.data())) : {}
        const requests = tallyRequests(requestSnap.docs.map((d: any) => d.data()))
        const recognition = recognitionStats(recogSnap.docs.map((d: any) => d.data()))

        // latest cached narrative per person for this period (Summary column)
        const pictureDocs = latestPictureDocs(picturesSnap.docs.map((d: any) => d.data()))

        // manager-review count per person, windowed to the active period (Evidence column)
        const boostsByPerson = tallyBoostsByPerson(boostsSnap.docs.map((d: any) => d.data()), range.start, range.end)

        // single pass over develop_skills; the per-user loop below is O(users)
        const journeyMap = tallyJourneys(state.journeyDocs, range.start, range.end)
        const nowMs = Date.now()
        const rows: any[] = []
        for (const emailKey in allUser) {
          const member = allUser[emailKey]
          if (!member) continue
          const email = member.email ? member.email : emailKey.replace(/\|\|/g, '.')
          const cur = current[emailKey]
          const journeys = journeyMap[emailKey] || { completed: 0, active: 0, learningMinutes: 0, categories: {} }
          const feedbackEvents = cur ? cur.received : 0
          const hash = computeInputHash({
            feedbackEvents,
            journeys: journeys.completed + journeys.active,
            recognitionEvents: recognition[emailKey] ? recognition[emailKey].received : 0,
          })
          rows.push({
            emailKey,
            email,
            firstname: member.firstname,
            lastname: member.lastname,
            profilepic: member.profilepic,
            team: member.team || '',
            teamname: member.team && teams[member.team] ? teams[member.team].name : '',
            ratingavg: cur ? cur.ratingavg : null,
            ratingDelta: range.comparable ? ratingDelta(cur, prior[emailKey]) : null,
            topStrength: cur ? topMention(cur.excels) : null,
            topImprove: cur ? topMention(cur.improves) : null,
            journeysCompleted: journeys.completed,
            journeysActive: journeys.active,
            feedbackEvents,
            giverCount: cur ? cur.giverCount : 0,
            managerReviews: boostsByPerson[emailKey] || 0,
            inputHash: hash,
            summaryStatus: narrativeStatus(pictureDocs[emailKey] || null, hash, feedbackEvents, nowMs, boostsByPerson[emailKey] || 0),
          })
        }
        const payload = {
          range,
          rows: _.orderBy(rows, [
            (r: any) => (r.teamname || '').toLowerCase(),
            (r: any) => (r.firstname || '').toLowerCase(),
          ], ['asc', 'asc']),
          current, prior, requests, recognition, pictureDocs,
        }
        state.evidenceCache[period] = payload
        return payload
      })()
      evidenceRequests[period] = request
      try {
        const payload = await request
        if (state.period !== period) return // superseded — cached for later, don't clobber live state
        commit('setEvidence', payload)
      } catch (error) {
        console.error('Error fetching development evidence:', error)
        if (state.period !== period) return // superseded — don't clobber the live period's state
        commit('setEvidence', {
          range: null, rows: [], current: {}, prior: {},
          requests: {}, recognition: {}, pictureDocs: {},
        })
      } finally {
        if (evidenceRequests[period] === request) delete evidenceRequests[period]
        if (state.period === period) commit('setLoading', false)
      }
    },

    // ---- person page (v2) ----

    // All-time evidence for one person, loaded once per person: the 6M/12M
    // toggle filters CLIENT-SIDE (helpers in @/utils/developmentPicture) so it
    // never refetches. Every query pairs two equality filters (company +
    // person field) — no composite indexes needed.
    async getPersonEvidence({ state, commit, rootState }: any, { email }: any) {
      const company = rootState.authen.user.company
      const emailKey = email.replace(/\./g, '||')
      // session short-circuit — re-opening the same person skips 5–17 reads
      // (mirrors evidenceCache). Runs BEFORE the picture reset so a cache hit
      // leaves the loaded person's picture state intact.
      if (state.personKeyLoaded === emailKey && state.personEvidence) return
      commit('bumpPersonRequest')
      const requestId = state.personRequestId
      // couple picture state to the person being loaded: clear the previous
      // person's cached narrative so B never briefly shows A's picture. The
      // component then dispatches loadPicture to refill this slot.
      commit('setPersonPictureDoc', null)
      commit('setPictureStatus', 'none')
      commit('setPersonLoading', true)
      try {
        const fs = firebase.firestore()
        const [fbSnap, gemSnap, jySnap, recogSnap, toneSnap] = await Promise.all([
          fs.collection('peerfeedback').where('company', '==', company).where('email_to', '==', email).get(),
          fs.collection('send_review_gem').where('company', '==', company).where('receiver', '==', email).get(),
          fs.collection('develop_skills').where('company', '==', company).where('email', '==', email).get(),
          fs.collection('here_point').where('company', '==', company).where('send_to_email', '==', email).get(),
          fs.collection('toneanalyze').where('email', '==', email).where('power_skill', '==', 'ps_self_awareness').get(),
        ])
        // Self-reflections: toneanalyze tags which questions fed Self Awareness;
        // answers live in users_questions. Join per recent quest_id (bounded reads).
        const range = personPeriodRange(12) // widest possible window so the 6M/12M toggle never refetches
        const questIds: string[] = []
        toneSnap.forEach((d: any) => {
          const v = d.data()
          if (v.company === company && v.quest_id && String(v.date || '') >= range.prevStart && questIds.indexOf(v.quest_id) < 0) questIds.push(v.quest_id)
        })
        const reflectionDocs: any[] = []
        await Promise.all(questIds.slice(0, 12).map(qid =>
          fs.collection('users_questions').where('users', '==', email).where('quest_id', '==', qid).get()
            .then((s: any) => s.forEach((d: any) => { const v = d.data(); if (v.company === company) reflectionDocs.push(v) }))
        ))
        if (state.personRequestId !== requestId) return // superseded by a newer person
        const rows = (snap: any) => { const out: any[] = []; snap.forEach((d: any) => out.push(d.data())); return out }
        commit('setPersonEvidence', {
          emailKey,
          evidence: {
            feedbackAll: rows(fbSnap),
            boostDocs: rows(gemSnap),
            journeyDocs: rows(jySnap),
            recognitionDocs: rows(recogSnap).filter((r: any) => r.statusrow === 'sendcoin'),
            reflectionDocs,
          },
        })
      } finally {
        if (state.personRequestId === requestId) commit('setPersonLoading', false)
      }
    },

    // Read the cached narrative doc for person+period. Never generates —
    // a miss lands on 'not_generated' and the component shows the Generate CTA.
    async loadPicture({ state, commit, rootState }: any, { email }: any) {
      const company = rootState.authen.user.company
      const emailKey = email.replace(/\./g, '||')
      const periodKey = `${state.personMonths}m`
      commit('setPictureStatus', 'loading')
      try {
        const doc = await firebase.firestore().collection('development_pictures')
          .doc(`${company}_${emailKey}_${periodKey}`).get()
        if (!doc.exists) {
          commit('setPersonPictureDoc', null)
          commit('setPictureStatus', 'not_generated')
          return
        }
        const data: any = doc.data()
        const valid = data && data.email === email && data.summary && typeof data.summary === 'object'
        commit('setPersonPictureDoc', valid ? data : null)
        // staleness refined in the component via inputHash
        commit('setPictureStatus', valid ? 'fresh' : 'not_generated')
      } catch (e) {
        commit('setPersonPictureDoc', null)
        commit('setPictureStatus', 'not_generated')
      }
    },

    // On-demand ONLY (spec decision 13): reachable solely from explicit
    // Generate/Regenerate buttons — never dispatched on page open.
    async generateNarrative({ state, commit, rootState }: any, { email, inputHash, provenance }: any) {
      const company = rootState.authen.user.company
      const emailKey = email.replace(/\./g, '||')
      const periodKey = `${state.personMonths}m`
      commit('bumpPersonRequest')
      const requestId = state.personRequestId
      commit('setPictureStatus', 'generating')
      try {
        const apiUrl = process.env.VUE_APP_API_URL || 'https://api-node.happily.ai'
        // Shared-secret gate on the endpoint (see happily-api ApiKeyGuard). The key
        // ships in the client bundle by nature of a browser app, so this blocks
        // anonymous callers rather than authenticating the individual viewer.
        const devSummaryKey = process.env.VUE_APP_DEV_SUMMARY_KEY || ''
        const res = await axios.post(
          `${apiUrl}/ai/generate_development_summary`,
          { company, person: email, periodKey },
          { headers: { 'x-api-key': devSummaryKey } }
        )
        const summary = res.data && res.data.summary
        if (!summary || typeof summary !== 'object') throw new Error('bad narrative shape')
        const docBody = {
          company,
          email,
          periodKey,
          summary,
          provenance: provenance || res.data.provenance || {},
          generatedAt: new Date().toISOString(),
          model: res.data.model || '',
          inputHash: inputHash || '',
          createdAtMs: Date.now(),
        }
        await firebase.firestore().collection('development_pictures')
          .doc(`${company}_${emailKey}_${periodKey}`).set(docBody)
        // Keep the already-loaded roster/cache current. This avoids a full
        // multi-query reload when the user returns from the person report.
        commit('upsertGeneratedPicture', docBody)
        if (state.personRequestId !== requestId || state.personKeyLoaded !== emailKey) return
        commit('setPersonPictureDoc', docBody)
        commit('setPictureStatus', 'fresh')
      } catch (e) {
        if (state.personRequestId === requestId) commit('setPictureStatus', 'error')
      }
    },
  },
}
